import type { Mood } from "../api/types";
import { theme } from "../theme";

/** Compact relative time, e.g. "now", "3m", "2h", "4d". Input is epoch milliseconds. */
export function relativeTime(epochMs: number): string {
  const deltaMs = Date.now() - epochMs;
  const s = Math.max(0, Math.floor(deltaMs / 1000));
  if (s < 45) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

/** Countdown to an epoch-milliseconds instant, e.g. "in 5h 12m" / "now". */
export function countdownTo(epochMs: number): string {
  const deltaS = Math.floor((epochMs - Date.now()) / 1000);
  if (deltaS <= 0) return "now";
  const h = Math.floor(deltaS / 3600);
  const m = Math.floor((deltaS % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Compact count for stat tiles, e.g. "842", "12.4k", "1.2M" (trailing ".0" dropped). */
export function formatCompact(n: number): string {
  if (n < 1000) return String(n);
  const scaled = (v: number, suffix: string) =>
    `${v.toFixed(1).replace(/\.0$/, "")}${suffix}`;
  if (n < 1_000_000) return scaled(n / 1000, "k");
  return scaled(n / 1_000_000, "M");
}

const MOOD_LABEL: Record<Mood, string> = {
  asleep: "asleep",
  grumpy: "grumpy",
  uneasy: "uneasy",
  happy: "happy",
  curious: "curious",
};

const MOOD_EMOJI: Record<Mood, string> = {
  asleep: "😴",
  grumpy: "😤",
  uneasy: "😟",
  happy: "😄",
  curious: "🤔",
};

export function moodLabel(mood: Mood): string {
  return MOOD_LABEL[mood] ?? mood;
}

export function moodEmoji(mood: Mood): string {
  return MOOD_EMOJI[mood] ?? "";
}

// Dot/label color for a mood, used on the DM "choose a chat" rows.
const MOOD_COLOR: Record<Mood, string> = {
  happy: theme.color.good,
  curious: theme.color.accent,
  uneasy: theme.color.happiness,
  grumpy: theme.color.danger,
  asleep: theme.color.asleep,
};

/** Accent color for a mood (a dead pet reads as the muted asleep tone). */
export function moodColor(mood: Mood, dead = false): string {
  if (dead) return theme.color.asleep;
  return MOOD_COLOR[mood] ?? theme.color.textDim;
}
